import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, withRouter } from 'react-router-dom'
import Cookies from 'js-cookie'
import toast from 'utils/toast'
import queryString from 'query-string'

import Spinner from 'britive-design-system/core/components/spinner'
import BritiveLogo from 'components/BritiveLogo'
import {
  authenticateUser,
  sendOneTimePasswordEmail,
  setIsInitialAuthing,
  updatePassword,
} from 'action_creators/account'
import {
  parseRequestOptionsFromJSON,
  get as webAuthNGet,
  create,
  parseCreationOptionsFromJSON,
} from '@github/webauthn-json/browser-ponyfill'

import { updateAuthenticationInfo } from 'action_creators/account'
import { getUserPure } from 'action_creators/user'
import doFetch, { preFetch } from 'utils/do_fetch'
import {
  MFA_SETUP,
  RESET_PASSWORD,
  SOFTWARE_TOKEN_MFA,
  VALIDATE_USER,
  MFA_WEB_AUTHN_SETUP,
  WEB_AUTHN_MFA,
  MFA_REGISTER_CHOOSE_FACTOR,
  MFA_AUTH_CHOOSE_FACTOR,
  mfaFactor,
} from 'constants/authentication'

import LoginForm from './components/LoginForm'
import MFAForm from './components/MFAForm'
import ResetPasswordForm from './components/ResetPasswordForm'
import ChangePasswordForm from './components/ChangePasswordForm'
import { handleCLIRedirect } from 'utils/handle-cli-redirect'
import SSO from './components/SSO'
import get from 'lodash/get'

const initialValues = {
  username: '',
  password: '',
  oneTimePassword: '',
  newPassword: '',
  confirmPassword: '',
  currentPassword: '',
  mfaOtp: '',
  webAuthnName: '',
  webAuthnDescription: '',
}
const LoginRoutes = ({
  errorMessage,
  dispatch,
  history,
  onSubmit,
  accessToken,
  refreshToken,
  challenge,
  resetChallenge,
  challengeParameters,
  identityProviders,
  isInitialAuthing,
}) => {
  const [fields, setFields] = useState(initialValues)

  const [error, setError] = useState(null)
  const [isEmailSentModalOpen, setIsEmailSentModalOpen] = useState(false)
  const [showMFARegisterOption, setShowMFARegisterOption] = useState('')
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setError(errorMessage)
  }, [errorMessage])

  useEffect(() => {
    const pathnames = ['/login', '/login/reset-password']
    pathnames.includes(location?.pathname) && setFields(initialValues)
    setError(null)
  }, [location?.pathname])

  const redirectAfterLogin = () =>
    window.location.assign(location?.state?.from?.pathname || '/')

  useEffect(() => {
    setError(null)
  }, [challenge, showMFARegisterOption])

  const handleLoginSubmit = async event => {
    setError(null)
    event.preventDefault()
    if (fields.username && (fields.password || challenge === VALIDATE_USER)) {
      setLoading(true)
      try {
        await onSubmit(fields)
        setTimeout(
          () => {
            setLoading(false)
          },
          challenge === VALIDATE_USER ? 0 : 2000
        )
      } catch (error) {
        setError(error?.response?.data?.message)
        setLoading(false)
      }
    }
  }

  const handleFieldChange = event => {
    const { name, value } = event.target
    setFields(fields => ({
      ...fields,
      [name]: value,
    }))
    setError(null)
  }

  const sendOTPEmail = async event => {
    event.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await dispatch(sendOneTimePasswordEmail(fields.username))
      setIsEmailSentModalOpen(true)
    } catch (error) {
      setError(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  const changePassword = async (event, challenge) => {
    setError(null)
    event.preventDefault()
    const { newPassword, oneTimePassword, username, currentPassword } = fields
    let response
    setLoading(true)
    const authParameters = {
      username,
      session: challengeParameters?.session,
      password: newPassword,
      challenge,
      ...(challenge === RESET_PASSWORD
        ? { code: oneTimePassword }
        : { currentPassword }),
    }

    try {
      response = await dispatch(updatePassword(authParameters))

      await dispatch(updateAuthenticationInfo(response.value))

      if (
        ![
          SOFTWARE_TOKEN_MFA,
          MFA_SETUP,
          MFA_WEB_AUTHN_SETUP,
          MFA_REGISTER_CHOOSE_FACTOR,
          MFA_AUTH_CHOOSE_FACTOR,
        ].includes(
          response?.value?.data?.authenticationResult?.challengeParameters?.challenge
        )
      ) {
        const userResponse = await preFetch({
          name: 'getUser',
          path: '/access/users',
          method: 'get',
        })

        await dispatch(getUserPure(userResponse))
        Cookies.set('signInMethod', 'login')
        redirectAfterLogin()
        return
      }

      history.push('/login/mfa', {
        from: location?.state?.from,
      })
    } catch (error) {
      setError(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  const registerOTP = async () => {
    try {
      await dispatch(
        authenticateUser({
          authParameters: {
            username: fields.username,
            challenge: MFA_REGISTER_CHOOSE_FACTOR,
            mfa_factor_chosen: mfaFactor.totp,
            session: challengeParameters.session,
          },
        })
      )
      history.push('/login/mfa', {
        from: location?.state?.from,
      })
    } catch (error) {
      setError(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
    dispatch(setIsInitialAuthing(false))
  }

  const submitMFAOTP = async event => {
    setError(null)
    event.preventDefault()
    setLoading(true)
    if (
      challengeParameters?.challenge === MFA_REGISTER_CHOOSE_FACTOR ||
      showMFARegisterOption === mfaFactor.webAuthN
    ) {
      registerOTP()
      return
    }
    try {
      const fromCli = sessionStorage.getItem('cli')
      const response = await dispatch(
        authenticateUser({
          authParameters: {
            username: fields.username,
            code: fields.mfaOtp,
            challenge:
              challengeParameters.challenge === MFA_SETUP
                ? MFA_SETUP
                : SOFTWARE_TOKEN_MFA,
            oauth2state: challengeParameters.oauth2state,
            session: challengeParameters.session,
            ...(challengeParameters.challenge === MFA_SETUP && {
              AccessToken: accessToken,
              RefreshToken: refreshToken,
            }),
            ...(fromCli && { cliToken: fromCli, type: 'CLI' }),
          },
        })
      )

      Cookies.set('signInMethod', 'login')

      const oauth2state = sessionStorage.getItem('oauth2state')
      const oauthRedirectUri = get(
        response,
        'data.authenticationResult.challengeParameters.oauthRedirectUri'
      )
      if (oauthRedirectUri && oauth2state) {
        window.location.assign(oauthRedirectUri)
        return
      }

      if (handleCLIRedirect(response, history)) {
        if (response) {
          await dispatch(updateAuthenticationInfo(response))
        }

        dispatch(setIsInitialAuthing(true))
        redirectAfterLogin()
      }
    } catch (error) {
      setError(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
    dispatch(setIsInitialAuthing(false))
  }

  const handleAbortErrorForWebAuthn = error => {
    if (['AbortError', 'NotAllowedError'].includes(error?.name)) {
      setLoading(false)
      return null
    }
    setError(error?.response?.data?.message)
    setLoading(false)
  }

  const authenticateUserDispatch = async authParameters => {
    return await dispatch(authenticateUser({ authParameters }))
  }

  const handleWebAuthNChallenge = async (
    challengeParameters,
    publicKeyCredential
  ) => {
    const response = await authenticateUserDispatch({
      username: fields.username,
      challenge: WEB_AUTHN_MFA,
      webauthn_request_options_id: challengeParameters?.webauthn_request_options_id,
      webauthn_pk_credentials_json: JSON.stringify(publicKeyCredential),
      session: challengeParameters.session,
    })
    Cookies.set('signInMethod', 'login')
    if (handleCLIRedirect(response, history)) {
      if (response) {
        await dispatch(updateAuthenticationInfo(response))
      }
      dispatch(setIsInitialAuthing(true))
      redirectAfterLogin()
    }
  }

  const registerWebAuthN = async challengeParameters => {
    try {
      let initialResponse
      if (challengeParameters?.challenge !== MFA_WEB_AUTHN_SETUP) {
        initialResponse = await authenticateUserDispatch({
          username: fields.username,
          challenge: MFA_REGISTER_CHOOSE_FACTOR,
          mfa_factor_chosen: mfaFactor.webAuthN,
          session: challengeParameters.session,
        })
      }

      const creationOptions = parseCreationOptionsFromJSON(
        JSON.parse(
          challengeParameters?.challenge === MFA_WEB_AUTHN_SETUP
            ? challengeParameters?.webauthn_creation_options_json
            : initialResponse?.data?.authenticationResult?.challengeParameters
                ?.webauthn_creation_options_json
        )
      )

      const publicKeyCredential = await create(creationOptions)
      if (!publicKeyCredential) return

      const creationOptionsId =
        challengeParameters?.challenge === MFA_WEB_AUTHN_SETUP
          ? challengeParameters?.webauthn_creation_options_id
          : initialResponse?.data?.authenticationResult?.challengeParameters
              ?.webauthn_creation_options_id

      const registerResponse = await authenticateUserDispatch({
        username: fields.username,
        challenge: MFA_WEB_AUTHN_SETUP,
        webauthn_credential_friendly_name: fields.webAuthnName,
        webauthn_credential_description: fields.webAuthnDescription,
        webauthn_creation_options_id: creationOptionsId,
        webauthn_pk_credentials_json: JSON.stringify(publicKeyCredential),
        session: challengeParameters.session,
      })
      toast({
        title: `WebAuthn device registration successful! You will soon be prompted to authenticate.`,
        type: 'success',
        time: 'normal',
      })
      const requestOptions = parseRequestOptionsFromJSON(
        JSON.parse(
          registerResponse?.data?.authenticationResult?.challengeParameters
            ?.webauthn_request_options_json
        )
      )

      const finalPublicKeyCredential = await webAuthNGet(requestOptions)
      if (!finalPublicKeyCredential)
        throw new Error('Cannot complete webauthn authentication.')

      await handleWebAuthNChallenge(
        registerResponse?.data?.authenticationResult?.challengeParameters,
        finalPublicKeyCredential
      )
    } catch (error) {
      handleAbortErrorForWebAuthn(error)
    } finally {
      dispatch(setIsInitialAuthing(false))
    }
  }

  const submitMFAWebAuthn = async event => {
    setError(null)
    event.preventDefault()
    setLoading(true)

    if (
      challengeParameters?.challenge === MFA_REGISTER_CHOOSE_FACTOR ||
      challengeParameters?.challenge === MFA_WEB_AUTHN_SETUP ||
      showMFARegisterOption === mfaFactor.webAuthN
    ) {
      registerWebAuthN(challengeParameters)
      return
    }

    try {
      const initialResponse = await authenticateUserDispatch({
        username: fields.username,
        challenge: MFA_AUTH_CHOOSE_FACTOR,
        mfa_factor_chosen: mfaFactor.webAuthN,
        session: challengeParameters.session,
      })

      const requestOptions = parseRequestOptionsFromJSON(
        JSON.parse(
          initialResponse?.data?.authenticationResult?.challengeParameters
            ?.webauthn_request_options_json
        )
      )

      const publicKeyCredential = await webAuthNGet(requestOptions)
      if (!publicKeyCredential)
        throw new Error('Cannot complete webauthn authentication.')

      await handleWebAuthNChallenge(
        initialResponse?.data?.authenticationResult?.challengeParameters,
        publicKeyCredential
      )
    } catch (error) {
      handleAbortErrorForWebAuthn(error)
    } finally {
      dispatch(setIsInitialAuthing(false))
    }
  }

  const initSSO = async event => {
    setError(null)
    event?.preventDefault()
    const { search } = location
    const oauth2state = sessionStorage.getItem('oauth2state')
    setLoading(true)
    try {
      const response = await doFetch({
        path: `/auth/sso-url${search}${
          oauth2state ? `&oauth2state=${oauth2state}` : ''
        }`,
        method: 'GET',
        allow401: true,
      })

      // logs to debug PAB-14204
      console.log('sso-url response')
      console.log(response)

      if (!response) {
        history.push('/login')
        return
      }

      const { loginUrl, logoutUrl } = response.data

      const cliToken = sessionStorage.getItem('cli')
      const params = queryString.parse(location.search)

      let britiveIdpLoginUrl = loginUrl

      if (cliToken) {
        britiveIdpLoginUrl = `${loginUrl}&token=${cliToken}`
      } else if (params?.redirectUrl) {
        britiveIdpLoginUrl = `${loginUrl}&redirectUrl=${params?.redirectUrl}`
      }

      sessionStorage.setItem('ssoSignIn', britiveIdpLoginUrl)
      sessionStorage.setItem('ssoSignOut', logoutUrl)

      window.location.assign(britiveIdpLoginUrl)
    } catch (error) {
      setError(error?.response?.data?.message)
      setLoading(false)
    }
  }

  const ssoURL = sessionStorage.getItem('ssoSignIn')

  return (
    <div className="login-container">
      <div className="section">
        <BritiveLogo height={32} />
        {isInitialAuthing ? (
          <>
            <div className="loader"></div>
            <Spinner size="medium" message="Loading" />
          </>
        ) : (
          <div className="form-container">
            {loading && (
              <>
                <div className="loader"></div>
                <Spinner size="medium" />
              </>
            )}
            <Switch>
              <Route
                exact
                path="/login/sso"
                render={props => (
                  <SSO {...props} errorMessage={error} initSSO={initSSO} />
                )}
              />
              <Route
                exact
                path="/login/reset-password"
                render={props => (
                  <ResetPasswordForm
                    {...props}
                    loading={loading}
                    fields={fields}
                    handleChange={handleFieldChange}
                    sendOTPEmail={sendOTPEmail}
                    errorMessage={error}
                    isEmailSentModalOpen={isEmailSentModalOpen}
                    changePassword={changePassword}
                    setIsEmailSentModalOpen={setIsEmailSentModalOpen}
                  />
                )}
              />

              <Route
                exact
                path="/login/change-password"
                render={props => (
                  <ChangePasswordForm
                    {...props}
                    loading={loading}
                    fields={fields}
                    handleChange={handleFieldChange}
                    errorMessage={error}
                    changePassword={changePassword}
                  />
                )}
              />

              <Route
                exact
                path="/login/mfa"
                render={props => (
                  <MFAForm
                    {...props}
                    loading={loading}
                    submitMFAOTP={submitMFAOTP}
                    submitMFAWebAuthn={submitMFAWebAuthn}
                    errorMessage={error}
                    fields={fields}
                    handleChange={handleFieldChange}
                    showMFARegisterOption={showMFARegisterOption}
                    setShowMFARegisterOption={setShowMFARegisterOption}
                  />
                )}
              />

              <Route
                exact
                path=""
                render={props => (
                  <LoginForm
                    {...props}
                    loading={loading}
                    fields={fields}
                    challenge={challenge}
                    resetChallenge={resetChallenge}
                    ssoURL={ssoURL}
                    errorMessage={error}
                    handleSubmit={handleLoginSubmit}
                    handleChange={handleFieldChange}
                    identityProviders={identityProviders}
                  />
                )}
              />
            </Switch>
          </div>
        )}
      </div>
      <div className="section">
        <aside className="login-side-image" />
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  challengeParameters: state?.account?.challengeParameters,
  isInitialAuthing: state?.account?.isInitialAuthing,
  accessToken: state?.account?.accessToken,
  refreshToken: state?.account?.refreshToken,
})

export default withRouter(connect(mapStateToProps)(LoginRoutes))
